import { usageGuard } from './guards/usage.guard';
import { Router, RouterModule, Routes, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs/operators';
import { inject, NgModule } from '@angular/core';
import { UserLayoutComponent } from './components/layouts/user-layout.component';
import { GuestLayoutComponent } from './components/layouts/guest-layout.component';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { beheerderGuard } from './guards/beheerder.guard';
import { PermissionGuard } from './guards/permission.guard';
import { EModulePermissions, Permission} from './enums/permission.enum';
import { environmentPermissionGuard } from './guards/environment-permission.guard';
import { EnvironmentPermission } from './enums/environment-permission.enum';
import { ClubAdminGuard } from './guards/club-admin.guard';
import { ledenGuard } from './guards/leden.guard';
import { clubAlbumsGuard } from './guards/club-albums.guard';
import { TaggingService } from './services/tagging.service';
import { PermissionService } from './services/permission.service';


const routes: Routes = [
  // Already shared playlists to redirect to new one
  {path: 'playlists/public/:id', redirectTo: 'auth/playlists/public/:id', pathMatch: 'full'},
  {path: 'player-invite/:id', redirectTo: 'auth/player-invite/:id', pathMatch: 'full'},
  // De herstelmail verwijst naar /RecoverPassword/<token>; de schermen zelf
  // zitten onder /auth.
  {path: 'RecoverPassword/:token', redirectTo: 'auth/recover-password/:token', pathMatch: 'full'},

  {
    path: 'auth',
    component: GuestLayoutComponent,
    children: [
      {
        path: 'login',
        canActivate: [guestGuard],
        loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule),
        data: { hideCrisp: true },
      },
      {
        path: 'player-invite',
        canActivate: [guestGuard],
        loadChildren: () => import('./pages/player-invite/player-invite.module').then(m => m.PlayerInviteModule),
        data: { hideCrisp: true },
      },
      {
        // Aanvragen vanaf het inlogscherm (21-10-2026): club aanmelden of
        // een account bij je club. Gaat als mail naar ons.
        path: 'register-club',
        canActivate: [guestGuard],
        loadComponent: () => import('./pages/account-aanvragen/account-aanvragen.component').then(m => m.AccountAanvragenComponent),
        data: { hideCrisp: true, soort: 'club' },
      },
      {
        path: 'request-account',
        canActivate: [guestGuard],
        loadComponent: () => import('./pages/account-aanvragen/account-aanvragen.component').then(m => m.AccountAanvragenComponent),
        data: { hideCrisp: true, soort: 'member' },
      },
      {
        path: 'recover-password',
        loadChildren: () => import('./pages/recover-password/recover-password.module').then(m => m.RecoverPasswordModule),
        data: { hideCrisp: true },
      },
      {
        path: 'forgot-password',
        canActivate: [guestGuard],
        loadChildren: () => import('./pages/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule),
        data: { hideCrisp: true },
      },
      {
        path: 'playlists',
        loadChildren: () => import('./pages/playlists/playlists.module').then(m => m.PlaylistsModule),
        data: { hideCrisp: true },
      },
      {
        // Clubkiezer (24-10-2026): een super-admin logt met zijn eigen
        // account in en kiest hier een club, in plaats van in te loggen met
        // een gedeeld clubaccount.
        path: 'kies-club',
        canActivate: [beheerderGuard],
        loadComponent: () => import('./pages/kies-club/kies-club.component').then(m => m.KiesClubComponent),
        data: { hideCrisp: true },
      },
      {
        // "Open als club" vanuit de beheerconsole: token in het URL-fragment.
        // Bewust zonder guestGuard: de beheerder is hier vaak al ingelogd
        // als een andere club, en die sessie wordt juist vervangen.
        path: 'open-als-club',
        loadComponent: () => import('./pages/open-als-club/open-als-club.component').then(m => m.OpenAlsClubComponent),
      },
      {
        // Inloggen bij een club app via PlayStats (docs/PARTNERS.md, stap 5).
        // Zonder guard: ingelogd toont het de vraag, anders eerst inloggen.
        path: 'connect',
        loadComponent: () => import('./pages/club-app-connect/club-app-connect.component').then(m => m.ClubAppConnectComponent),
        data: { hideCrisp: true },
      },
      {path: '**', redirectTo: '/auth/login', pathMatch: 'full'},
    ]
  },
  {
    path: '',
    component: UserLayoutComponent,
    canActivate: [authGuard],
    // Een speler (club_player) heeft alleen zijn eigen profiel en zijn
    // account (docs/SPELERSPROFIEL.md stap 3). De gateway laat hem nergens
    // anders meer bij; zonder dit kwam hij op een lege of kapotte pagina,
    // en een onbekend adres stuurde hem naar /videos.
    canActivateChild: [(_: unknown, state: RouterStateSnapshot) => {
      if (!inject(PermissionService).isSpeler()) {
        return true;
      }
      const eerste = state.url.split(/[/?#]/)[1] ?? '';
      return ['', 'me', 'profile', 'welcome'].includes(eerste) || inject(Router).parseUrl('/me');
    }],
    children: [
      // Beginpagina: wedstrijden-tabel (25-09-2026). Video's = kaarten/beeld.
      // Een taggingteam (tagger en beheerder) heeft geen eigen wedstrijden:
      // dat begint bij Tagging. Of de club een taggingteam is, vraagt de
      // beheerder op; daarom een guard en geen vaste doorverwijzing.
      {
        path: '',
        pathMatch: 'full',
        canActivate: [() => {
          const router = inject(Router);
          // Een speler begint op zijn eigen profiel (docs/SPELERSPROFIEL.md).
          if (inject(PermissionService).isSpeler()) {
            return router.parseUrl('/me');
          }
          return inject(TaggingService).isTaggingTeam().pipe(map(team => router.parseUrl(team ? '/tagging' : '/matches')));
        }],
        children: [],
      },
      {
        // Opdrachten van een taggingteam (docs/TAGGER-OPDRACHTEN.md).
        path: 'tagging',
        canActivate: [() => inject(TaggingService).isTagger() || inject(TaggingService).isBeheerder()],
        loadComponent: () => import('./pages/tagging/tagging.component').then(m => m.TaggingComponent),
      },
      {
        path: 'live',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/live/live.module'),
        data: {
          permissions: [Permission.LIVE_SHOW],
          modulePermission: EModulePermissions.SCHEDULING
        }
      },
      {
        path: 'videos',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/videos/videos.module').then(m => m.VideosModule),
        data: {
          permissions: [Permission.VIDEO_SHOW],
          modulePermission: EModulePermissions.VIDEO_STORAGE,
        }
      },
      {
        path: 'schedules',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/schedules/schedules.module').then(m => m.SchedulesModule),
        data: {
          permissions: [Permission.RECORDERS_SCHEDULE_SHOW],
          modulePermission: EModulePermissions.SCHEDULING
        }
      },
      {
        path: 'matches',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/match/match.module').then(m => m.MatchModule),
        data: {
          permissions: [Permission.MATCH_SHOW]
        }
      },
      {
        path: 'players',
        canActivate: [environmentPermissionGuard, PermissionGuard],
        loadChildren: () => import('./pages/players/players.module').then(m => m.PlayersModule),
        data: {
          permissions: [Permission.PLAYERS_SHOW],
        }
      },
      {
        // Spelerprofiel (optie A, 04-10-2026): één speler over de laatste
        // wedstrijden, met ?naam= zoals de data hem geeft. Vóór 'search'.
        path: 'search/speler',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadComponent: () => import('./pages/zoeken/speler-profiel/speler-profiel.component').then(m => m.SpelerProfielComponent),
        data: {
          permissions: [Permission.MATCH_SHOW],
          modulePermission: EModulePermissions.VIDEO_STORAGE,
          // Video-pagina: zonder kopregel en menubalk, zoals de videopagina
          // (eigenaar 25-10-2026). Alleen vanaf 992 px, zie user-layout.
          hideHeader: 'breed',
        }
      },
      {
        // Momenten zoeken in de laatste wedstrijden (Figma Header Type=Search).
        path: 'search',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadComponent: () => import('./pages/zoeken/zoeken.component').then(m => m.ZoekenComponent),
        data: {
          permissions: [Permission.MATCH_SHOW],
          modulePermission: EModulePermissions.VIDEO_STORAGE,
          hideHeader: 'breed',
        }
      },
      {
        // De hele wedstrijd voor een speler, als de staf die met hem deelde.
        // Alleen het beeld, via /Players/Me (de videopagina van de staf toont
        // alle events met namen).
        path: 'me/match/:sid',
        loadComponent: () => import('./pages/zoeken/mijn-wedstrijd/mijn-wedstrijd.component').then(m => m.MijnWedstrijdComponent),
      },
      {
        // Een clip die de staf de speler stuurde (docs/SPELERSPROFIEL.md
        // stap 5), met zijn reactie. De gateway (/Players/Me/Clips) geeft
        // alleen zijn eigen deel.
        path: 'me/clip/:sid',
        loadComponent: () => import('./pages/zoeken/mijn-clip/mijn-clip.component').then(m => m.MijnClipComponent),
      },
      {
        // Het eigen profiel van een speler: wat de staf met hem deelde. De
        // gateway (/Players/Me) bepaalt wat hij ziet; geen rechtencontrole hier.
        path: 'me',
        loadComponent: () => import('./pages/zoeken/speler-profiel/speler-profiel.component').then(m => m.SpelerProfielComponent),
        data: { mij: true },
      },
      {
        // "Complete your profile": één keer na het aannemen van een
        // uitnodiging (helpers/profiel-aanvullen.ts), voor speler en staf.
        path: 'welcome',
        loadComponent: () => import('./pages/welkom/welkom.component').then(m => m.WelkomComponent),
      },
      {
        path: 'profile',
        loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'panels',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/panels/panels.module').then(m => m.PanelsModule),
        data: {
          // Panelen beheren, niet alleen ermee taggen: een coach heeft wel
          // events_show_schema (om te taggen) maar niet het bewerkrecht.
          permissions: [Permission.EVENTS_EDIT_SCHEMA],
        }
      },
      {
        path: 'upload',
        canActivate: [PermissionGuard, environmentPermissionGuard],
        loadChildren: () => import('./pages/upload/upload.module').then(m => m.UploadModule),
        data: {
          permissions: [Permission.UPLOAD_REQUEST],
          modulePermission: EModulePermissions.VIDEO_STORAGE
        }
      },
      {
        path: 'club-albums',
        canActivate: [environmentPermissionGuard, clubAlbumsGuard],
        loadChildren: () => import('./pages/club-album/club-album.module').then(m => m.ClubAlbumModule),
        data: {
          environmentPermissions: [EnvironmentPermission.CLUB_ALBUM]
        }
      },
      {
        // Leden importeren uit Excel/CSV (hele club, spelers en staf): alleen
        // de club_admin. Vóór 'users', anders pakt die het pad.
        path: 'users/import',
        canActivate: [environmentPermissionGuard, ClubAdminGuard],
        loadComponent: () => import('./pages/leden/leden-import/leden-import.component').then(m => m.LedenImportComponent),
      },
      {
        // Users (25-10-2026: de oude Users en Team members samen): club_admin
        // met "All teams" erbij, en teambeheerders voor hun eigen teams; auth
        // beslist per team wat mag.
        path: 'users',
        canActivate: [environmentPermissionGuard, ledenGuard],
        loadComponent: () => import('./pages/leden/leden.component').then(m => m.LedenComponent),
      },
      // De oude adressen van Team members, voor bladwijzers en mails.
      { path: 'members/import', redirectTo: 'users/import' },
      { path: 'members', redirectTo: 'users' },
      {
        // Usage (01-11-2026): de clubbeheerder, of met het vinkje "Can see usage".
        path: 'monitoring',
        canActivate: [environmentPermissionGuard, usageGuard],
        loadChildren: () => import('./pages/monitoring/monitoring.module').then(m => m.MonitoringModule),
      },
      {
        // Event data per partner (docs/EVENT-DATA.md): per team aanzetten en
        // de clubkaart. Alleen de club_admin (en een super-admin die de club open heeft).
        path: 'event-data',
        canActivate: [environmentPermissionGuard, ClubAdminGuard],
        loadComponent: () => import('./pages/event-data/event-data.component').then(m => m.EventDataComponent),
      },
      {
        path: '',
        canActivate: [PermissionGuard],
        data: {
          permissions: [Permission.PLAYLISTS_SHOW],
          modulePermission: EModulePermissions.VIDEO_STORAGE
        },
        loadChildren: () => import('./pages/playlists/playlists.module').then(m => m.PlaylistsModule)
      },
      {path: '**', redirectTo: '/videos', pathMatch: 'full'},
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
